import React from 'react'
import {
    useUltraPlayStore,
    useConfigurationStore,
    useBetSliderStore,
    useSpinButtonSectionStore,
    usePlayerInfoStore,
    useBuyBonusStore,
    useDoubleChanceStore,
    useSoundStore,
    ULTRA_PLAY_SPIN_OPTIONS,
    ULTRA_PLAY_DOUBLE_CHANCE,
} from '@stores'
import { useThrottledCallback } from '@hooks'
import { SOUNDS } from '@constants/sounds'
import {
    AutoplayButtonClickedMessage,
    BetSliderSelectedIndexChangedMessage,
    BetOptionItem,
    BuyBonusButtonClickedMessage,
    BuyBonusConfirmButtonClickedMessage,
    BuyBonusOptionButtonClickedMessage,
    DoubleChanceButtonClickedMessage,
    EngineBetSliderSetScrollingIndexMessage,
    ExtraWinMessages,
    ExtraWinToggledMessage,
    GameUIMessageScope,
    GameUIMessageType,
    SetSpeedButtonLevelMessage,
    StopAutoplayClickedMessage,
    ViewportType,
    BetSliderScrollStartedMessage,
    BuyBonusRejectedMessage,
} from 'game-ui-types'
import { collectUltraPlayWin } from './ultraPlayCollect'
import { executeUltraPlayPause, flushPendingUltraPlayPause, isUltraPlayPauseBlocked } from './ultraPlayPause'
import { playUltraPlaySound, ULTRA_PLAY_SOUND } from './ultraPlaySound'
import { largestFiniteEngineIndex } from './ultraPlaySpinRecorder'

// ── Helpers ───────────────────────────────────────────────────────────────────

const getBetAmount = (betOptions: number[] | BetOptionItem[], index: number): number => {
    if (!betOptions.length) return 0

    const clamped = Math.max(0, Math.min(index, betOptions.length - 1))
    const option = betOptions[clamped]

    return typeof option === 'number' ? option : option.betAmount
}

const findClosestEngineIndex = (engineOptions: number[], targetCount: number): number => {
    if (!engineOptions.length) return 0

    // Infinite: drive the engine with its largest finite batch instead of the endless option, which
    // emits no per-spin count (so the Ultra Play table would never record). The session re-triggers.
    if (targetCount === -1) {
        return largestFiniteEngineIndex(engineOptions)
    }

    return engineOptions.reduce((best, count, i) => {
        if (count === -1) return best

        const bestCount = engineOptions[best] === -1 ? Infinity : engineOptions[best]

        return Math.abs(count - targetCount) < Math.abs(bestCount - targetCount) ? i : best
    }, 0)
}

// All Ultra Play controls logic: store wiring, derived view state and the play/pause/bet/spins/buy-
// bonus handlers. Extracted from UltraPlayControls so that component is purely presentational.
export const useUltraPlayControls = () => {
    const status = useUltraPlayStore((s) => s.status)
    const spins = useUltraPlayStore((s) => s.spins)
    const totalBet = useUltraPlayStore((s) => s.totalBet)
    const totalWin = useUltraPlayStore((s) => s.totalWin)
    const remainingSpins = useUltraPlayStore((s) => s.remainingSpins)
    const currentSpinNumber = useUltraPlayStore((s) => s.currentSpinNumber)
    const selectedBetIndex = useUltraPlayStore((s) => s.selectedBetIndex)
    const setSelectedBetIndex = useUltraPlayStore((s) => s.setSelectedBetIndex)
    const selectedSpinsIndex = useUltraPlayStore((s) => s.selectedSpinsIndex)
    const setSelectedSpinsIndex = useUltraPlayStore((s) => s.setSelectedSpinsIndex)
    const stopOnFeature = useUltraPlayStore((s) => s.stopOnFeature)
    const setStopOnFeatureState = useUltraPlayStore((s) => s.setStopOnFeature)
    const isNoConnection = useUltraPlayStore((s) => s.isNoConnection)
    const isInFeature = useUltraPlayStore((s) => s.isInFeature)
    const isPauseInFlight = useUltraPlayStore((s) => s.isPauseInFlight)
    const isBetInFlight = useUltraPlayStore((s) => s.isBetInFlight)
    const isPausePending = useUltraPlayStore((s) => s.isPausePending)
    const selectedBuyBonusFeatureName = useUltraPlayStore((s) => s.selectedBuyBonusFeatureName)
    const setSelectedBuyBonusFeatureName = useUltraPlayStore((s) => s.setSelectedBuyBonusFeatureName)
    const start = useUltraPlayStore((s) => s.start)
    const continueSession = useUltraPlayStore((s) => s.continueSession)
    const resume = useUltraPlayStore((s) => s.resume)
    const extendRemainingSpins = useUltraPlayStore((s) => s.extendRemainingSpins)

    const betOptions = useConfigurationStore((s) => s.betOptions)
    const setConfiguration = useConfigurationStore((s) => s.setConfiguration)
    const currency = useConfigurationStore((s) => s.currency)
    const hideCents = useConfigurationStore((s) => s.currencyHideCents)
    const hasBuyBonus = useConfigurationStore((s) => s.hasBuyBonus)
    const hasDoubleChance = useConfigurationStore((s) => s.hasDoubleChance)
    const doubleChanceMultiplier = useConfigurationStore((s) => s.doubleChanceMultiplier)
    const viewport = useConfigurationStore((s) => s.viewport)
    const paytableData = useConfigurationStore((s) => s.paytableData)
    const gameName = (paytableData.gameName as string) || ''

    const buyBonusFeatures = useBuyBonusStore((s) => s.buyBonusConfig.features)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((s) => s.updateCurrentFeatureMultiplier)
    const setMultiChoiceToggle = useBuyBonusStore((s) => s.setMultiChoiceToggle)
    const updateActiveBetIndex = useBetSliderStore((s) => s.updateActiveIndex)
    const updateScrollToIndex = useBetSliderStore((s) => s.updateScrollToIndex)
    const playEngineSound = useSoundStore((s) => s.playEngineSound)
    // Same drag feedback as the autoplay-settings sliders: a rate-limited engine "tick" sound, distinct
    // from the steppers' own click sounds.
    const triggerSliderDragSound = useThrottledCallback(() => playEngineSound(SOUNDS.BET_SLIDE), 50)
    const isSetup = status === 'READY' && spins.length === 0
    const isPlaying = status === 'PLAYING'
    const isPaused = status === 'PAUSED'
    const isActive = isPlaying || isPaused
    const isHistory = status === 'READY' && spins.length > 0
    const isPortrait = viewport === ViewportType.MOBILE_PORTRAIT
    const isLandscape = viewport === ViewportType.MOBILE_LANDSCAPE
    // Lock all controls (bet / spins sliders, Stop on Feature, Buy Bonus, Play) while a feature is
    // playing out — nothing can change mid-feature — while the connection is down, and while a pause is
    // still in flight (its STOP_AUTOPLAY_CLICKED hasn't been confirmed by the engine yet) — clicking Play
    // in that window would race a new autoplay batch against the still-in-flight stop.
    const controlsDisabled = isNoConnection || (isInFeature && isPlaying) || isPauseInFlight

    const playPauseDisabled = isNoConnection || isPauseInFlight

    // Portrait only: the gear in the footer reveals the feature settings (Stop on Feature + Buy Bonus)
    // above the play button. Stays open while the footer is shown (playing/paused and the finished
    // history view); only resets when we return to the setup screen.
    const [settingsOpen, setSettingsOpen] = React.useState(false)

    React.useEffect(() => {
        if (isSetup) setSettingsOpen(false)
    }, [isSetup])

    // In the finished (history) view the spins display stays at the completed set (e.g. 10/10) until
    // the player picks a new count — then it previews the next total (the value a fresh start shows).
    const [hasPickedNewSpins, setHasPickedNewSpins] = React.useState(false)

    React.useEffect(() => {
        if (!isHistory) setHasPickedNewSpins(false)
    }, [isHistory])

    const currentBetAmount = getBetAmount(betOptions, selectedBetIndex)
    const currentSpinsCount = ULTRA_PLAY_SPIN_OPTIONS[selectedSpinsIndex] ?? 20
    const totalSpins = currentSpinNumber + remainingSpins

    const spinsValue = isActive
        ? `${currentSpinNumber}/${currentSpinsCount === -1 ? '∞' : totalSpins}`
        : isHistory
          ? hasPickedNewSpins
              ? `${currentSpinNumber}/${currentSpinsCount === -1 ? '∞' : currentSpinNumber + currentSpinsCount}`
              : `${currentSpinNumber}/${currentSpinNumber}`
          : String(currentSpinsCount === -1 ? '∞' : currentSpinsCount)

    // Spins step only through the predefined options. While playing you can't lower the total below
    // the spins already played — the floor is the smallest option still above the current spin count.
    const canIncrementSpins = selectedSpinsIndex < ULTRA_PLAY_SPIN_OPTIONS.length - 1
    const canDecrementSpins =
        selectedSpinsIndex > 0 && (!isActive || ULTRA_PLAY_SPIN_OPTIONS[selectedSpinsIndex - 1] > currentSpinNumber)

    // ── Buy Bonus bet options ─────────────────────────────────────────────────
    // Save original betOptions before any buy bonus selection so we can restore on "OFF"

    const originalBetOptionsRef = React.useRef<typeof betOptions | null>(null)

    // On unmount (modal closed) restore the game's base betOptions. Ultra Play rewrites betOptions to
    // reflect a feature's multiplied bet, but the game shows the multiplied bet via currentFeature
    // multiplier instead — so the array itself must go back to base. The multiplier / selection state
    // is intentionally preserved here: it is carried over to the game on close (see UltraPlayModal).
    React.useEffect(() => {
        return () => {
            if (originalBetOptionsRef.current) {
                useConfigurationStore.getState().setConfiguration({ betOptions: originalBetOptionsRef.current })
            }
        }
    }, [])

    // Rewrite betOptions to base × multiplier so the Ultra Play bet display / recorder reflect the
    // feature cost. The base array is captured once so repeated selections always multiply from base.
    const applyMultiplierToBetOptions = (multiplier: number) => {
        if (originalBetOptionsRef.current === null) {
            originalBetOptionsRef.current = betOptions
        }

        const originals = originalBetOptionsRef.current
        const multiplied = originals.map((opt) =>
            typeof opt === 'number'
                ? opt * multiplier
                : { ...opt, betAmount: (opt as { betAmount: number }).betAmount * multiplier }
        ) as typeof betOptions

        setConfiguration({ betOptions: multiplied })
    }

    const collectWin = collectUltraPlayWin

    // Reconcile the engine's Double Chance toggle to a desired on/off state. The engine flips its
    // internal state on every DOUBLE_CHANCE_BUTTON_CLICKED, so only send when it actually differs
    // (isDoubleChanceActivated mirrors the engine state) — otherwise the toggle would invert.
    const setEngineDoubleChance = (desired: boolean) => {
        const { isDoubleChanceActivated, setIsDoubleChanceActivated } = useDoubleChanceStore.getState()

        if (isDoubleChanceActivated === desired) return

        setIsDoubleChanceActivated(desired)
        window.postMessage(
            {
                type: GameUIMessageType.DOUBLE_CHANCE_BUTTON_CLICKED,
                scope: GameUIMessageScope.DOUBLE_CHANCE,
            } as DoubleChanceButtonClickedMessage,
            '*'
        )
        collectWin()
    }

    const handleBuyBonusChange = (featureName: string) => {
        if (isActive && isUltraPlayPauseBlocked()) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.INFO_2)

        if (featureName === 'OFF') {
            if (originalBetOptionsRef.current) {
                setConfiguration({ betOptions: originalBetOptionsRef.current })
            }

            updateCurrentFeatureMultiplier(1)
            setMultiChoiceToggle(false)

            const buyBonusRejectedMessage: BuyBonusRejectedMessage = {
                type: GameUIMessageType.BUY_BONUS_REJECTED,
            }

            window.postMessage(buyBonusRejectedMessage)
        } else if (featureName === ULTRA_PLAY_DOUBLE_CHANCE) {
            // Double Chance: multiplies the bet like a buy bonus, but is a bet-mode toggle (not a
            // multi-choice buy bonus) — the engine toggle is reconciled on play / resume / close.
            applyMultiplierToBetOptions(doubleChanceMultiplier)
            updateCurrentFeatureMultiplier(doubleChanceMultiplier)
            setMultiChoiceToggle(false)
        } else {
            const feature = buyBonusFeatures.find((f) => f.name === featureName)

            if (feature) {
                applyMultiplierToBetOptions(feature.multiplier)
                updateCurrentFeatureMultiplier(feature.multiplier)
                setMultiChoiceToggle(true)
            }
        }

        setSelectedBuyBonusFeatureName(featureName)

        // During an active session a buy bonus change pauses the game (like a bet change); the new
        // selection is sent to the engine on resume. Stop on Feature changes never pause. Mirrors
        // sendBetChange: lock the controls until the engine confirms the stop (else Resume could race a
        // new autoplay batch against it), and collect first so the in-flight spin's pending win is
        // credited — the recorder finalizes the paused bet on its real result, not a premature 0.
        if (isActive) {
            executeUltraPlayPause()
        }
    }

    // On open, reflect the game's active feature (Double Chance or a selected buy bonus) into the
    // dropdown + bet, so the two stay in sync. Runs once when the controls mount (i.e. modal opened);
    // the engine is already in this state, so this only mirrors the display — no engine messages.
    React.useEffect(() => {
        const { isDoubleChanceActivated } = useDoubleChanceStore.getState()
        const { isMultiChoiceToggled, selectedFeature } = useBuyBonusStore.getState()

        if (hasDoubleChance && isDoubleChanceActivated) {
            handleBuyBonusChange(ULTRA_PLAY_DOUBLE_CHANCE)
        } else if (isMultiChoiceToggled && selectedFeature) {
            handleBuyBonusChange(selectedFeature.name)
        }
    }, [])

    const sendBuyBonusToEngine = (featureName: string, betIdx: number) => {
        const features = useConfigurationStore.getState().buyBonusConfig.features
        const featureIndex = features.findIndex((f) => f.name === featureName)

        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_BUTTON_CLICKED,
                scope: GameUIMessageScope.BUY_BONUS,
            } as BuyBonusButtonClickedMessage,
            '*'
        )
        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_OPTION_BUTTON_CLICKED,
                featureId: featureName,
            } as BuyBonusOptionButtonClickedMessage,
            '*'
        )
        window.postMessage(
            {
                type: GameUIMessageType.BET_SLIDER_SET_SCROLLING_INDEX,
                currentBetIndex: betIdx,
                shouldPositionList: true,
            } as EngineBetSliderSetScrollingIndexMessage,
            '*'
        )
        updateScrollToIndex(betIdx)
        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_CONFIRM_BUTTON_CLICKED,
                selectedFeatureIndex: featureIndex,
                betIndex: betIdx,
                selectedFeatureName: featureName,
                scope: GameUIMessageScope.BUY_BONUS,
            } as BuyBonusConfirmButtonClickedMessage,
            '*'
        )
    }

    // Reconcile the engine to the selected feature before autoplay starts: enable/disable Double
    // Chance, or send the buy bonus purchase. Called from play and resume.
    const syncFeatureToEngine = (featureName: string, betIdx: number) => {
        setEngineDoubleChance(featureName === ULTRA_PLAY_DOUBLE_CHANCE)

        if (featureName !== 'OFF' && featureName !== ULTRA_PLAY_DOUBLE_CHANCE) {
            sendBuyBonusToEngine(featureName, betIdx)
        }
    }

    // ── Handlers ─────────────────────────────────────────────────────────────
    const sendBetChange = (newIndex: number) => {
        window.postMessage(
            {
                scope: GameUIMessageScope.BET_SLIDER,
                type: GameUIMessageType.BET_SLIDER_SELECTED_INDEX_CHANGED,
                index: newIndex,
                isScrolling: false,
            } as BetSliderSelectedIndexChangedMessage,
            '*'
        )
    }

    const handleBetChangedWhileActive = (newIndex: number) => {
        executeUltraPlayPause()
        sendBetChange(newIndex)
    }

    // Apply a bet index from any control (slider or +/-): update it and, during an active session,
    // pause and sync to the engine — so sliding pauses Ultra Play just like the steppers do.
    // Keep BetSlider (which stays mounted, just hidden, behind Ultra Play) in sync live, including
    // its scroll position — otherwise closing Ultra Play (or pressing Play) without this can leave the
    // slider highlighting the right bet but scrolled to a different one, or showing the pre-Ultra-Play
    // bet entirely if the change never reached this store.
    const applyBetIndexChange = (newIndex: number) => {
        if (isActive && isUltraPlayPauseBlocked()) return

        setSelectedBetIndex(newIndex)
        updateActiveBetIndex(newIndex)
        updateScrollToIndex(newIndex)

        if (isActive) {
            handleBetChangedWhileActive(newIndex)
        } else {
            sendBetChange(newIndex)
        }
    }

    // Slider drag only (the stepper buttons call applyBetIndexChange directly, with their own sound).
    // The no-op guard keeps the slider's continuous onChange from re-pausing on every tick.
    const handleBetIndexChange = (newIndex: number) => {
        if (newIndex === useUltraPlayStore.getState().selectedBetIndex) return

        triggerSliderDragSound()
        applyBetIndexChange(newIndex)
    }

    const handleBetDecrement = () => {
        if (selectedBetIndex <= 0) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.DECREASE_BET)
        applyBetIndexChange(selectedBetIndex - 1)
    }

    const handleBetIncrement = () => {
        if (selectedBetIndex >= betOptions.length - 1) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.INCREASE_BET)
        applyBetIndexChange(selectedBetIndex + 1)

        const scrollStartedMessage: BetSliderScrollStartedMessage = {
            scope: GameUIMessageScope.BET_SLIDER,
            type: GameUIMessageType.BET_SLIDER_SCROLL_STARTED,
        }

        window.postMessage(scrollStartedMessage)
    }

    // During an active session, grow/shrink the live remaining count so the x/total display reacts
    // immediately. No engine call here — that is done via restartActiveAutoplay on commit/click.
    const extendActiveSpins = (index: number) => {
        const newCount = ULTRA_PLAY_SPIN_OPTIONS[index]

        if (newCount === -1) return

        const { currentSpinNumber: num, remainingSpins: rem } = useUltraPlayStore.getState()
        const newRemaining = Math.max(1, newCount - num)

        extendRemainingSpins(newRemaining - rem)
    }

    // Re-issue autoplay so the engine picks up the new spins count.
    const restartActiveAutoplay = () => {
        if (isUltraPlayPauseBlocked()) return

        useUltraPlayStore.getState().setPendingAutoplayRestart(true)
        window.postMessage({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage, '*')
    }

    // Spins slider drag: update the count live (display + remaining). The engine re-sync is deferred
    // to commit (slider release) so dragging doesn't restart autoplay on every tick.
    const handleSpinsIndexChange = (newIndex: number) => {
        if (newIndex === selectedSpinsIndex) return

        triggerSliderDragSound()
        setSelectedSpinsIndex(newIndex)

        if (isHistory) setHasPickedNewSpins(true)

        if (isActive) extendActiveSpins(newIndex)
    }

    // Spins slider release: now re-sync the engine to the chosen count.
    const handleSpinsCommit = () => {
        if (isPlaying && ULTRA_PLAY_SPIN_OPTIONS[useUltraPlayStore.getState().selectedSpinsIndex] !== -1) {
            restartActiveAutoplay()
        }
    }

    const handleSpinsDecrement = () => {
        if (selectedSpinsIndex <= 0) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.NUMBER_SPINS)

        const newIndex = selectedSpinsIndex - 1

        setSelectedSpinsIndex(newIndex)

        if (isHistory) setHasPickedNewSpins(true)

        if (isActive) {
            extendActiveSpins(newIndex)

            if (isPlaying && ULTRA_PLAY_SPIN_OPTIONS[newIndex] !== -1) restartActiveAutoplay()
        }
    }

    const handleSpinsIncrement = () => {
        if (selectedSpinsIndex >= ULTRA_PLAY_SPIN_OPTIONS.length - 1) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.NUMBER_SPINS)

        const newIndex = selectedSpinsIndex + 1

        setSelectedSpinsIndex(newIndex)

        if (isHistory) setHasPickedNewSpins(true)

        if (isActive) {
            extendActiveSpins(newIndex)

            if (isPlaying && ULTRA_PLAY_SPIN_OPTIONS[newIndex] !== -1) restartActiveAutoplay()
        }
    }

    const handlePlay = () => {
        // No connection — play is disabled in the UI, but guard here too so nothing slips through.
        if (useUltraPlayStore.getState().isNoConnection) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.PLAY)

        // Not enough funds for the selected bet — show the modal instead of starting a session.
        if (currentBetAmount > 0 && usePlayerInfoStore.getState().balance < currentBetAmount) {
            useUltraPlayStore.getState().setShowInsufficientBalance(true)

            return
        }

        syncFeatureToEngine(useUltraPlayStore.getState().selectedBuyBonusFeatureName, selectedBetIndex)

        const engineOptions = useConfigurationStore.getState().autoplayOptions
        const engineIndex = findClosestEngineIndex(engineOptions, currentSpinsCount)

        window.postMessage(
            { type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED, payload: engineIndex } as AutoplayButtonClickedMessage,
            '*'
        )

        const sbStore = useSpinButtonSectionStore.getState()

        sbStore.updateAutoplayMode(true)
        sbStore.updateAutoplayCount(currentSpinsCount !== -1 ? currentSpinsCount - 1 : currentSpinsCount)
        sbStore.updateSelectedAutoplayIndex(engineIndex)

        const currentSpeed = useSpinButtonSectionStore.getState().speedButtonLevel

        useUltraPlayStore.getState().setPreviousSpeedLevel(currentSpeed)

        // Jump straight to max speed for Ultra Play (no need to simulate Speed button clicks).
        const maxSpeedLevel = useConfigurationStore.getState().shouldEnableUltraSpeed ? 3 : 2

        window.postMessage(
            { type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL, speedLevel: maxSpeedLevel } as SetSpeedButtonLevelMessage,
            '*'
        )

        // From a finished session (history) → continue: keep the table, totals and spin counter and
        // just add the newly selected spins on top (10/10 + 10 → 11/20; + 50 → 11/60). Fresh otherwise.
        if (isHistory) {
            continueSession(currentSpinsCount)
        } else {
            start(usePlayerInfoStore.getState().balance, currentSpinsCount)
        }
    }

    React.useEffect(() => {
        flushPendingUltraPlayPause()
    }, [isPausePending, isBetInFlight, isInFeature, status, isPauseInFlight])

    const handlePlayPause = () => {
        const up = useUltraPlayStore.getState()

        if (up.isNoConnection) return

        // A pause is still settling with the engine — ignore extra clicks (the button is also disabled
        // via controlsDisabled, this is a defensive second guard) so Resume can't race the in-flight stop.
        if (up.isPauseInFlight) return

        if (isPlaying) {
            // Prevent pausing at the penultimate spin because the last one may already be in-flight.
            // Avoids pausing at 10 out of 10 or similar. Instead of PAUSED the desired state is READY.
            if (up.remainingSpins !== -1 && up.remainingSpins - (up.hasUnrecordedBet ? 1 : 0) <= 0) return

            playUltraPlaySound(ULTRA_PLAY_SOUND.INFO_1)

            if (isUltraPlayPauseBlocked()) {
                // We try to disable autoplay now because otherwise next bet may be made before the pause takes effect.
                window.postMessage({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage, '*')
                up.setPausePending(true)

                return
            }

            executeUltraPlayPause()
        } else if (isPaused) {
            // The balance may have dropped (e.g. a server sync) while paused — don't resume into an
            // unaffordable bet (which crashes the engine); re-show the insufficient-balance modal.
            if (currentBetAmount > 0 && usePlayerInfoStore.getState().balance < currentBetAmount) {
                useUltraPlayStore.getState().setShowInsufficientBalance(true)

                return
            }

            const { remainingSpins: pausedRemaining, hasUnrecordedBet } = useUltraPlayStore.getState()
            const effectiveRemaining = pausedRemaining === -1 ? -1 : pausedRemaining - (hasUnrecordedBet ? 1 : 0)

            if (effectiveRemaining !== -1 && effectiveRemaining <= 0) {
                useUltraPlayStore.getState().stop()

                return
            }

            playUltraPlaySound(ULTRA_PLAY_SOUND.PLAY)
            resume()

            const { selectedBuyBonusFeatureName: resumeBuyBonus } = useUltraPlayStore.getState()
            const engineOptions = useConfigurationStore.getState().autoplayOptions
            const resumeEngineIndex = findClosestEngineIndex(engineOptions, effectiveRemaining)

            syncFeatureToEngine(resumeBuyBonus, selectedBetIndex)

            window.postMessage(
                {
                    type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED,
                    payload: resumeEngineIndex,
                } as AutoplayButtonClickedMessage,
                '*'
            )

            const sbStore = useSpinButtonSectionStore.getState()

            sbStore.updateAutoplayMode(true)
            sbStore.updateAutoplayCount(effectiveRemaining === -1 ? -1 : effectiveRemaining - 1)
            sbStore.updateSelectedAutoplayIndex(resumeEngineIndex)
        } else {
            handlePlay()
        }
    }

    // Stop on Feature drives the Extra Win splash: with it OFF, Ultra Play plays through features fast
    // so Extra Win stays disabled; with it ON, a feature closes Ultra Play back to the game, so Extra
    // Win is re-enabled for normal in-game play.
    const setStopOnFeature = (value: boolean) => {
        setStopOnFeatureState(value)
        window.postMessage(
            {
                type: ExtraWinMessages.EXTRA_WIN_TOGGLED,
                payload: { status: value ? 'enabled' : 'disabled' },
            } as ExtraWinToggledMessage,
            '*'
        )
    }

    return {
        // view state
        status,
        totalBet,
        totalWin,
        currency,
        hideCents,
        gameName,
        viewport,
        isSetup,
        isPlaying,
        isLandscape,
        isPortrait,
        currentBetAmount,
        spinsValue,
        selectedBetIndex,
        setSelectedBetIndex,
        selectedSpinsIndex,
        setSelectedSpinsIndex,
        betOptions,
        stopOnFeature,
        setStopOnFeature,
        isNoConnection,
        controlsDisabled,
        playPauseDisabled,
        isPausePending,
        selectedBuyBonusFeatureName,
        buyBonusFeatures,
        hasBuyBonus,
        hasDoubleChance,
        canDecrementSpins,
        canIncrementSpins,
        settingsOpen,
        setSettingsOpen,
        // handlers
        handleBetIndexChange,
        handleBetDecrement,
        handleBetIncrement,
        handleSpinsIndexChange,
        handleSpinsCommit,
        handleSpinsDecrement,
        handleSpinsIncrement,
        handleBuyBonusChange,
        handlePlayPause,
    }
}

export type UltraPlayControlsApi = ReturnType<typeof useUltraPlayControls>
